import { DestroyRef, inject, Injectable } from '@angular/core';
import { UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTime, Duration } from 'luxon';
import * as SparkMD5 from 'spark-md5';
import {
  API,
  CurrencyDTO,
  FileLink,
  IdentityDTO,
  ModifiableNamedIdentityWithIdentifierAndCreatorDTO,
  PlaybackScheduleSlotClientType,
  PlaybackScheduleSlotFullDTO,
  TrackingTechnique,
  VisitorTrackerDTO,
} from './api/api';
import { SbDateTime, SbLocalDateTime } from './api/api-date-time';
import { ErrorService } from './error.service';
import { GeoUtilService } from './geo/geoutil.service';
import { TranslateService } from '@ngx-translate/core';
import seatbackClient from '../../../package.json';
import { NavigationService, SbNavigationPathParam } from '@ngx-seatback/utils';
import { RoutingID } from '../routes/sb-routing-enum';
import { QueryParamService } from './lists/query-params';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from './authentication.service';
import { StringTransformationService } from './transformation-helper/string-transformation.service';
import { DateTransformationService } from './transformation-helper/date-transformation.service';

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly dateTransformation = inject(DateTransformationService);

  public RoutingID = RoutingID;

  public currencies: CurrencyDTO[] = [];
  public defaultCurrency: CurrencyDTO;

  constructor(
    private readonly router: Router,
    private readonly queryParamService: QueryParamService,
    private readonly authService: AuthenticationService,
    private readonly errorHandler: ErrorService,
    public readonly geo: GeoUtilService,
    private readonly translate: TranslateService,
    public readonly navService: NavigationService,
    private readonly api: API,
    private readonly destroyRef: DestroyRef
  ) {
    this.api
      ._public_PublicEndpoints_getCurrencies()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CurrencyDTO[]) => {
          this.currencies = res;
          if (this.currencies?.length > 0) {
            this.defaultCurrency = this.currencies[0];
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  getSeatbackCMSClient() {
    return seatbackClient.seatbackClient ?? 'generic';
  }

  arrayToIdArray<T extends IdentityDTO>(arr: T[]): any[] | null {
    if (arr && arr.length > 0) {
      const ret = [];
      for (const ele of arr) {
        if (ele?.id) {
          ret.push(ele.id);
        }
      }
      return ret;
    }
    return null;
  }

  computeChecksumMd5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const chunkSize = 2097152;
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();

      let cursor = 0;

      fileReader.onerror = (): void => {
        reject('MD5 computation failed - error reading the file');
      };

      function processChunk(chunkStart: number): void {
        const chunkEnd = Math.min(file.size, chunkStart + chunkSize);
        fileReader.readAsArrayBuffer(file.slice(chunkStart, chunkEnd));
      }

      fileReader.onload = (e: any): void => {
        spark.append(e.target.result);
        cursor += chunkSize;

        if (cursor < file.size) {
          processChunk(cursor);
        } else {
          resolve(spark.end());
        }
      };

      processChunk(0);
    });
  }

  downloadFileLink(fileLink: FileLink) {
    // window.location.href = fileLink.toString();
    // window.location.assign(fileLink.toString());

    const a = document.createElement('a');
    a.href = fileLink.toString();
    a.download = fileLink.toString(); // Optional: specify desired filename
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // window.open(fileLink.toString(), 'Download');
  }

  formatBytes(bytes, decimals = 2): string {
    if (bytes === 0 || !bytes) {
      return '0 bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  public convertHexToRGB(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  public convertRGBtoHex(color: string) {
    return (
      '#' +
      color
        .match(/\d+/g)
        .map(x => {
          x = parseInt(x, 10).toString(16);
          return x.length === 1 ? '0' + x : x;
        })
        .join('')
    );
  }

  public calcFontcolor(r: number, g: number, b: number) {
    if (r * 0.299 + g * 0.587 + b * 0.114 > 186) {
      return '#000000';
    } else {
      return '#FFFFFF';
    }
  }

  getPageLink() {
    return window.location.href.replace(window.location.origin, '');
  }

  returnToNavigation(fallback: string, pathParams?: SbNavigationPathParam | SbNavigationPathParam[]) {
    if (this.queryParamService.getQueryParam('returnTo')) {
      this.router.navigateByUrl(this.queryParamService.getQueryParam('returnTo')).then();
    } else {
      this.navService.navigateToPoint(fallback, pathParams);
    }
  }

  renderStringColor(stringToColor: string) {
    if (stringToColor === null || stringToColor === undefined) {
      return 'cecece';
    }
    switch (stringToColor) {
      case '.biG-AdR': {
        return '803ca1';
      }
      case 'Stress2': {
        return '68a3e5';
      }
      case 'Bymoot': {
        return 'eda338';
      }
      default: {
        return this.intToRGB(this.hashCode(stringToColor));
      }
    }
  }

  hashCode(str: string) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      // tslint:disable-next-line:no-bitwise
      hash = str.charCodeAt(i) + (hash << 5) - hash;
    }
    return hash;
  }

  intToRGB(i: number) {
    // tslint:disable-next-line:no-bitwise
    const c = (i & 0x00ffffff).toString(16).toUpperCase();

    return '00000'.substring(0, 6 - c.length) + c;
  }

  getSlotType(slot: PlaybackScheduleSlotFullDTO, type: string) {
    if (!slot) {
      return;
    }
    switch (slot.clientType) {
      case PlaybackScheduleSlotClientType.OWN: {
        if (type === 'Name') {
          return this.translate.instant('lang.schedules.ownSlot');
        } else if (type === 'Class') {
          return 'blue-bg slotHover';
        } else {
          return null;
        }
      }
      case PlaybackScheduleSlotClientType.FOREIGN: {
        if (type === 'Name') {
          return this.translate.instant('lang.schedules.foreignSlot');
        } else if (type === 'Class') {
          return 'red-bg slotHover';
        } else {
          return null;
        }
      }
      case PlaybackScheduleSlotClientType.MIXED: {
        if (type === 'Name') {
          return this.translate.instant('lang.schedules.mixedSlot');
        } else if (type === 'Class') {
          return 'yellow-bg slotHover';
        } else {
          return null;
        }
      }
      case PlaybackScheduleSlotClientType.FREE: {
        if (type === 'Name') {
          return this.translate.instant('lang.schedules.freeSlot');
        } else if (type === 'Class') {
          return 'green-bg';
        } else {
          return null;
        }
      }
      default: {
        if (type === 'Name') {
          return this.translate.instant('lang.schedules.undefinedSlot');
        } else if (type === 'Class') {
          return 'grey-bg';
        } else {
          return null;
        }
      }
    }
  }

  authorizedUrl(url: string) {
    if (!url) {
      return null;
    }
    if (url.length === 0) {
      return url;
    }
    return this.appendQueryParamToUrl(url, 'Authorization=' + this.authService.loadAuthorizationFromStorage());
  }

  appendQueryParamToUrl(url: string, append: string) {
    if (!url) {
      return null;
    }
    if (url.length === 0) {
      return url;
    }
    url = url + (url.indexOf('?') > -1 ? '&' : '?');
    url += append;
    return url;
  }

  calculateAspectRatio(width: number, height: number): string {
    if (width > 0 && height > 0) {
      const gcd = this.calculateGcd(width, height);
      return width / gcd + ':' + height / gcd;
    }
    return undefined;
  }

  calculateGcd(a: number, b: number) {
    if (b === 0) {
      return a;
    }
    return this.calculateGcd(b, a % b);
  }

  getTrackerIcon(tracker: VisitorTrackerDTO) {
    let icon: string = 'questionmark';
    if (tracker) {
      switch (tracker.technique) {
        case TrackingTechnique.BluetoothBeacon:
          icon = 'trackerBluetoothBeacon';
          break;
        case TrackingTechnique.CameraImage:
          icon = 'trackerCameraImage';
          break;
        case TrackingTechnique.Counting:
          icon = 'tracker';
          break;
        case TrackingTechnique.WLANTracker:
          icon = 'trackerWlanTracker';
          break;
        case TrackingTechnique.OpticalBarrier:
          icon = 'trackerOpticalBarrier';
          break;
        case TrackingTechnique.LiDARScanner:
          icon = 'trackerLidarScanner';
          break;
        case TrackingTechnique.ProcessData:
          icon = 'trackerProcessData';
          break;
        case TrackingTechnique.Study:
          icon = 'trackerStudy';
          break;
        case TrackingTechnique.ThermalImage:
          icon = 'trackerThermalImage';
          break;
        case TrackingTechnique.Turnstile:
          icon = 'trackerTurnstile';
          break;
        case TrackingTechnique.WLANAccessPoint:
          icon = 'devices';
          break;
        default:
          icon = 'questionmark';
      }
    }
    return icon;
  }

  /**
   * @deprecated use StringTransformationService's encodeSearchQueryParam instead
   */
  public encodeSearchQueryParam(exactMatch: boolean, search: string, wildcard?: string): string {
    // following statement can be removed in api calls with search function
    // this.options.search ? (this.options.search.length === 0 ? null :
    // this.util.encodeSearchQueryParam(this.options.exactMatch, this.options.search)) : null
    return this.stringTransformation.encodeSearchQueryParam(exactMatch, search, wildcard);
  }

  /**
   * @deprecated use StringTransformationService's encodeSearchQueryParam instead
   */
  getPaginationString(page: number, maxSize: number, currentCount: number, resultCount: number, itemName?: string) {
    return this.stringTransformation.getPaginationString(page, maxSize, currentCount, resultCount, itemName);
  }

  /**************************************************************************************************************


   dateTransformation methods



   ****************************************************************************************************************/

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public modifiedCheck<T extends ModifiableNamedIdentityWithIdentifierAndCreatorDTO>(dto: T): DateTime | null {
    return this.dateTransformation.modifiedCheck(dto);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public laterDate(firstDate: SbDateTime | null, secondDate: SbDateTime | null): DateTime | null {
    return this.dateTransformation.laterDate(firstDate, secondDate);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public earlierDate(firstDate: SbDateTime, secondDate: SbDateTime): DateTime {
    return this.dateTransformation.earlierDate(firstDate, secondDate);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public passedTime(date: SbDateTime, until?: string): string {
    return this.dateTransformation.passedTime(date, until);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public calculateTimespanBetweenDates(from: SbDateTime, till: SbDateTime): string {
    return this.dateTransformation.calculateTimespanBetweenDates(from, till);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  public parseTimeToDuration(time: number): string {
    return this.dateTransformation.parseTimeToDuration(time);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  endDateAfterStartDate: ValidatorFn = (fg: UntypedFormGroup) => {
    return this.dateTransformation.endDateAfterStartDate(fg);
  };

  /**
   * @deprecated use DateTransformationService's method instead
   */
  getFromDate(dateCount: number) {
    return this.dateTransformation.getFromDate(dateCount);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  dateToDateString(dateTime: DateTime) {
    return this.dateTransformation.dateToDateString(dateTime);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  dateToDateTimeString(dateTime: DateTime, showSeconds?: boolean) {
    return this.dateTransformation.dateToDateTimeString(dateTime, showSeconds);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  parseDateFromDateString(date: string) {
    return this.dateTransformation.parseDateFromDateString(date);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  getTimeframePercentage(startDate: any, endDate: any) {
    return this.dateTransformation.getTimeframePercentage(startDate, endDate);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  getTimeframeValue(startDate: any, endDate: any) {
    return this.dateTransformation.getTimeframeValue(startDate, endDate);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  localDateToJSDate(localDate: SbLocalDateTime | SbDateTime): Date {
    return this.dateTransformation.localDateToJSDate(localDate);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  parseDurationToSeconds(pDuration: Duration): number {
    return this.dateTransformation.parseDurationToSeconds(pDuration);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  getHourTimeSlotString(index: number): string {
    return this.dateTransformation.getHourTimeSlotString(index);
  }

  /**
   * @deprecated use DateTransformationService's method instead
   */
  getDayTranslation(day: number, length?: number): string {
    return this.dateTransformation.getDayTranslation(day, length);
  }

  /**
   * @deprecated use DateTransformationService's getMonthTranslation instead
   */
  getMonthTranslation(month: number, length?: number): string {
    return this.dateTransformation.getMonthTranslation(month, length);
  }
}
